<script setup lang="ts">
import { computed, ref } from 'vue'
import type { MediaAsset, MediaFolder } from '../../api/types'
import { ACCEPT, sizeLabel } from '../../lib/media'
import { filterMedia, tileMeta } from '../../lib/libraryView'
import type { UploadJob } from '../../stores/library'
import Icon from '../ui/Icon.vue'
import MediaThumb from './MediaThumb.vue'

/**
 * Grid of the image/video library — the settings screen (manage) and the composer's drawer / answer editor
 * (pick). Search by title, folder chips, upload button, drag & drop of files anywhere on the grid.
 */
const props = withDefaults(
  defineProps<{
    items: MediaAsset[]
    folders: MediaFolder[]
    selectable?: boolean
    selected?: string[]
    /** pick mode: files with a limit on this channel are not selectable and show the reason */
    channel?: 'line' | 'fb' | 'ig' | 'web' | null
    /** smaller tiles (composer drawer) */
    compact?: boolean
    /** in-progress / failed uploads shown as tiles */
    uploads?: UploadJob[]
    canUpload?: boolean
    loading?: boolean
    emptyText?: string
  }>(),
  { selectable: false, selected: () => [], channel: null, compact: false, uploads: () => [], canUpload: false, loading: false, emptyText: '' },
)
const emit = defineEmits<{ toggle: [asset: MediaAsset]; open: [asset: MediaAsset]; files: [files: File[]]; dismissUpload: [id: string] }>()

const q = ref('')
/** null = every folder */
const folder = ref<string | null>(null)
const input = ref<HTMLInputElement | null>(null)
const dragDepth = ref(0)

const shown = computed(() => filterMedia(props.items, { q: q.value, folder: folder.value }))
/** Finished uploads leave as soon as their file shows in the grid (no double tile). */
const jobs = computed(() =>
  props.uploads.filter((j) => !(j.status === 'done' && j.asset && props.items.some((m) => m.id === j.asset?.id))),
)
const total = computed(() => props.items.filter((m) => !m.deleted).length)
const thumb = computed(() => (props.compact ? 92 : 128))
const isSelected = (m: MediaAsset) => props.selected.includes(m.id)

function blockOf(m: MediaAsset): string | null {
  if (!props.selectable || !props.channel) return null
  return m.limits.find((l) => l.channel === props.channel)?.reason ?? null
}

function limitNote(m: MediaAsset): string | null {
  if (props.selectable) return null
  const l = m.limits[0]
  return l ? l.reason : null
}

function activate(m: MediaAsset): void {
  if (props.selectable) {
    if (blockOf(m)) return
    emit('toggle', m)
  } else emit('open', m)
}

function pick(): void {
  input.value?.click()
}

function onPicked(e: Event): void {
  const el = e.target as HTMLInputElement
  const files = [...(el.files ?? [])]
  el.value = ''
  if (files.length) emit('files', files)
}

const hasFiles = (e: DragEvent) => !!e.dataTransfer && [...e.dataTransfer.types].includes('Files')

function onDragEnter(e: DragEvent): void {
  if (!props.canUpload || !hasFiles(e)) return
  dragDepth.value++
}
function onDragOver(e: DragEvent): void {
  if (!props.canUpload || !hasFiles(e)) return
  e.preventDefault()
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
}
function onDragLeave(e: DragEvent): void {
  if (!props.canUpload || !hasFiles(e)) return
  dragDepth.value = Math.max(0, dragDepth.value - 1)
}
function onDrop(e: DragEvent): void {
  if (!props.canUpload || !hasFiles(e)) return
  e.preventDefault()
  dragDepth.value = 0
  const files = [...(e.dataTransfer?.files ?? [])]
  if (files.length) emit('files', files)
}

function uploadLine(j: UploadJob): string {
  if (j.status === 'error') return j.error ?? 'อัปโหลดไม่สำเร็จ'
  if (j.status === 'preparing') return 'กำลังเตรียมไฟล์'
  if (j.status === 'saving') return 'กำลังบันทึก'
  if (j.status === 'done') return 'อัปโหลดแล้ว'
  return `กำลังอัปโหลด ${Math.round(j.progress * 100)}%`
}

defineExpose({ folder, q })
</script>

<template>
  <div
    class="mg"
    :class="{ compact, drop: dragDepth > 0 }"
    @dragenter="onDragEnter"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <div class="mg-bar">
      <label class="mg-search">
        <Icon name="search" />
        <input v-model="q" type="search" placeholder="ค้นหาชื่อไฟล์" aria-label="ค้นหาชื่อไฟล์" />
      </label>
      <span class="grow"></span>
      <button v-if="canUpload" type="button" class="btn pri" @click="pick"><Icon name="upload" />อัปโหลด</button>
      <input ref="input" class="sr" type="file" :accept="ACCEPT" multiple tabindex="-1" aria-hidden="true" @change="onPicked" />
    </div>
    <div v-if="folders.length" class="mg-folders" role="group" aria-label="โฟลเดอร์">
      <button type="button" class="fchip" :class="{ on: folder === null }" :aria-pressed="folder === null" @click="folder = null">
        ทั้งหมด <span class="mono">{{ total }}</span>
      </button>
      <button
        v-for="f in folders"
        :key="f.name || '-'"
        type="button"
        class="fchip"
        :class="{ on: folder === f.name }"
        :aria-pressed="folder === f.name"
        @click="folder = f.name"
      >
        <Icon name="folder" />{{ f.name || 'ไม่มีโฟลเดอร์' }} <span class="mono">{{ f.count }}</span>
      </button>
    </div>

    <div class="mg-grid" :style="{ gridTemplateColumns: `repeat(auto-fill, ${thumb + 16}px)` }">
      <div v-for="j in jobs" :key="j.id" class="tile up" :class="{ bad: j.status === 'error' }" role="status">
        <span class="t-img" :style="{ width: `${thumb}px`, height: `${thumb}px` }">
          <img v-if="j.thumb" :src="j.thumb" alt="" />
          <Icon v-else :name="j.status === 'error' ? 'alertCircle' : j.kind === 'video' ? 'play' : 'image'" />
          <span v-if="j.status !== 'error'" class="t-bar"><span :style="{ width: `${Math.round(j.progress * 100)}%` }"></span></span>
        </span>
        <span class="t-title">{{ j.name }}</span>
        <span class="t-meta" :class="{ err: j.status === 'error' }">{{ uploadLine(j) }}</span>
        <span v-if="j.status !== 'error' && j.bytes" class="t-meta">{{ sizeLabel(j.bytes) }}</span>
        <span v-for="w in j.warnings" :key="w" class="t-warn">{{ w }}</span>
        <button v-if="j.status === 'error' || j.status === 'done'" type="button" class="t-dismiss" aria-label="ปิด" @click="emit('dismissUpload', j.id)">
          <Icon name="x" />
        </button>
      </div>

      <button
        v-for="m in shown"
        :key="m.id"
        type="button"
        class="tile"
        :class="{ sel: isSelected(m), off: !!blockOf(m) }"
        :aria-pressed="selectable ? isSelected(m) : undefined"
        :aria-disabled="blockOf(m) ? 'true' : undefined"
        :title="blockOf(m) ?? m.title"
        @click="activate(m)"
      >
        <span class="t-img" :style="{ width: `${thumb}px`, height: `${thumb}px` }">
          <MediaThumb :asset="m" :size="thumb" />
          <span v-if="selectable && isSelected(m)" class="t-check" aria-hidden="true"><Icon name="check" /></span>
        </span>
        <span class="t-title">{{ m.title || (m.kind === 'video' ? 'วิดีโอไม่มีชื่อ' : 'รูปไม่มีชื่อ') }}</span>
        <span class="t-meta">{{ tileMeta(m) }}</span>
        <span v-if="blockOf(m)" class="t-warn">{{ blockOf(m) }}</span>
        <span v-else-if="limitNote(m)" class="t-note">{{ limitNote(m) }}</span>
        <span v-if="!selectable && m.used_in.length" class="t-used">ใช้ใน {{ m.used_in.map((u) => `/${u.shortcut}`).join(' ') }}</span>
      </button>
    </div>

    <p v-if="!shown.length && !jobs.length && !loading" class="mg-empty">
      <template v-if="q || folder !== null">ไม่พบไฟล์ที่ตรงกับที่ค้นหา</template>
      <template v-else>{{ emptyText || 'ยังไม่มีรูปหรือวิดีโอในคลัง' }}</template>
      <template v-if="canUpload"> · ลากไฟล์มาวางที่นี่ หรือกดอัปโหลด</template>
    </p>
    <div v-if="dragDepth > 0" class="mg-dropmsg" aria-hidden="true"><Icon name="upload" />วางไฟล์เพื่ออัปโหลดเข้าคลัง</div>
  </div>
</template>

<style scoped>
.mg {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  border-radius: 12px;
}
.mg.drop {
  outline: 2px dashed #7fbfa2;
  outline-offset: 4px;
}
.mg-bar {
  display: flex;
  align-items: center;
  gap: 8px;
}
.mg-search {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  width: 280px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: 9px;
  background: #fff;
  color: var(--mut);
}
.mg-search :deep(.ic) {
  width: 15px;
  height: 15px;
}
.mg-search input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink);
}
.mg-search:focus-within {
  border-color: #9fcdb8;
}
.btn.pri {
  border-color: #9fcdb8;
  color: var(--ac2);
  font-weight: 600;
}
.btn.pri :deep(.ic) {
  color: var(--ac);
}
.mg-folders {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.fchip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: #fff;
  font-size: 12.5px;
  line-height: 20px;
  color: #2e3b35;
  white-space: nowrap;
}
.fchip :deep(.ic) {
  width: 13px;
  height: 13px;
  color: var(--mut);
}
.fchip .mono {
  font-size: 12px;
  color: var(--mut);
}
.fchip:hover {
  background: #f7f9f8;
}
.fchip.on {
  background: var(--acs);
  border-color: #9fcdb8;
  color: var(--ac2);
  font-weight: 600;
}
.fchip.on :deep(.ic),
.fchip.on .mono {
  color: var(--ac2);
}
.mg-grid {
  display: grid;
  gap: 16px 12px;
  align-items: start;
}
.tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 8px;
  border-radius: 12px;
  border: 1px solid transparent;
  text-align: left;
  min-width: 0;
}
button.tile:hover {
  background: #f7f9f8;
  border-color: var(--line2);
}
.tile.sel {
  background: var(--acs);
  border-color: #9fcdb8;
}
.tile.off {
  cursor: not-allowed;
}
.tile.off .t-img {
  opacity: 0.45;
}
.t-img {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #eef2f0;
  color: var(--mut);
  margin-bottom: 6px;
  overflow: hidden;
}
.t-img > img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.tile.sel .t-img {
  box-shadow: 0 0 0 2px var(--ac);
}
.t-check {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--ac);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px #fff;
}
.t-check :deep(.ic) {
  width: 14px;
  height: 14px;
  stroke-width: 2.6;
}
.t-bar {
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 8px;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.85);
  overflow: hidden;
}
.t-bar span {
  display: block;
  height: 100%;
  background: var(--ac);
  transition: width 0.2s;
}
.t-title {
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.t-meta,
.t-note {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.t-used {
  font-size: 12px;
  line-height: 18px;
  color: var(--ac2);
  overflow-wrap: anywhere;
}
.t-meta.err {
  color: var(--crit);
}
.t-note {
  color: #8a5100;
}
.t-warn {
  font-size: 12px;
  line-height: 18px;
  color: #8a5100;
}
.tile.up.bad {
  background: #fff8f7;
  border-color: #f2c7c2;
}
.t-dismiss {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--mut);
  background: #fff;
  box-shadow: 0 0 0 1px var(--line);
}
.t-dismiss :deep(.ic) {
  width: 14px;
  height: 14px;
}
.mg-empty {
  padding: 28px 16px;
  border: 1px dashed #cdd9d3;
  border-radius: 12px;
  color: var(--mut);
  font-size: 13px;
  line-height: 21px;
  text-align: center;
}
.mg-dropmsg {
  position: absolute;
  left: 50%;
  top: 12px;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--ac);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  pointer-events: none;
  white-space: nowrap;
}
.mg-dropmsg :deep(.ic) {
  width: 15px;
  height: 15px;
}
.mg.compact .t-title {
  font-size: 12.5px;
  line-height: 19px;
}
</style>
